import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import {
    DOCUMENTS,
    DOCUMENT_COLUMNS,
    QUERY_KEYS,
    ROUTES,
    documentPriorityOptions,
    documentStatusOptions,
} from '../../../constants';
import { getAllDocumentRequest, popup } from '../../../utils';
import { AdminPageHeader } from '../Common/AdminPagesHeader';
import { usePagination } from '../../../hooks';
import { setSidePanel } from '../../../redux';
import { Table } from '../../shared';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export const DocumentListing = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [response, setResponse] = useState({ data: [], count: 0 });
    const [filter, setFilter] = useState({ priority: '', status: '' });
    const [selectedId, setSelectedId] = useState({
        priorityId: '1',
        statusId: '1',
    });
    const [isFilterOpen, setIsFilterOpen] = useState({ priority: false, status: false });
    const [search, setSearch] = useState('');
    const pagination = usePagination(response.data.length, response.count);
    const csmCaseLoad = useSelector((s) => s.csmCaseload?.csmCaseloadData);

    const filterConfig = [
        {
            id: '1',
            name: 'priorityFilter',
            value: filter.priority,
            selectedId: selectedId.priorityId,
            handleFunction: (value, id) => {
                setFilter({ ...filter, priority: value });
                setSelectedId({ ...selectedId, priorityId: id });
            },
            isFilterOpen: isFilterOpen.priority,
            setIsFilterOpen: (value) => setIsFilterOpen((prev) => ({ ...prev, priority: value })),
            options: documentPriorityOptions,
        },
        {
            id: '2',
            name: 'statusFilter',
            value: filter.status,
            selectedId: selectedId.statusId,
            handleFunction: (value, id) => {
                setFilter({ ...filter, status: value });
                setSelectedId({ ...selectedId, statusId: id });
            },
            isFilterOpen: isFilterOpen.status,
            setIsFilterOpen: (value) => setIsFilterOpen((prev) => ({ ...prev, status: value })),
            options: documentStatusOptions,
        },
    ];

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, error, isError } = useQuery({
        queryKey: [
            QUERY_KEYS.DOCUMENTS,
            pagination.offset,
            pagination.limit,
            debouncedSearch,
            filter,
            csmCaseLoad?.id,
        ],
        queryFn: async () =>
            await getAllDocumentRequest({
                offset: pagination.offset,
                limit: pagination.limit,
                search: debouncedSearch,
                filter,
                id: csmCaseLoad?.id || '',
                role: csmCaseLoad?.role_code || '',
            }),
            staleTime : 0
    });
    if (isError) popup('error', error?.message);

    useEffect(() => {
        if (data) {
            setResponse({ data: data.data, count: data.count });
        }
    }, [data]);

    useEffect(() => {
        dispatch(setSidePanel({ sidePanel: false, id: '' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <AdminPageHeader
                heading={DOCUMENTS.PAGE_HEADING}
                buttonText={DOCUMENTS.BUTTON_TEXT}
                searchBarPlaceholder={DOCUMENTS.SEARCH_BAR_PLACEHOLDER}
                setSearch={setSearch}
                count={pagination.dataCount}
                showAddButton
                handleAddClick={() => navigate(ROUTES.ADMIN.DOCUMENT_REQUEST)}
                backbutton
                setCurrentPage={pagination.setCurrentPage}
                filterConfig={filterConfig}
            />
            <Table
                columns={DOCUMENT_COLUMNS}
                data={response.data}
                pagination={pagination}
                pageName={DOCUMENTS.PAGE_NAME}
                search={search}
                stickyColumns={['Documents']}
                isLoading={isFetching}
            />
        </>
    );
};
